import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiClient from "../../utils/apiClient"; // Corrected import path

// Define the Notification type (adjust properties based on actual data)
export interface Notification {
    notificationId: string;
    recipientId: string;
    initiatorId: string;
    initiatorName?: string; // Optional: Add if backend can provide name
    initiatorProfilePicture?: string; // Optional: Add if backend can provide pic
    type: "like" | "comment" | "connection_request" | "connection_accept" | "message";
    entityId: string; // ID of the post, connection, conversation etc.
    message: string; // Pre-formatted message from backend or construct on frontend
    status: "read" | "unread";
    createdAt: string | number; // ISO string or timestamp
}

// Export the state interface
export interface NotificationsState {
    notifications: Notification[];
    unreadCount: number;
    unreadMessageCount: number;
    unreadOtherCount: number;
    loading: boolean;
    error: string | null; // Error for fetching list/count
    statusLoading: { [notificationId: string]: boolean }; // Loading state per notification for status updates/deletes
    actionError: { notificationId: string; message: string } | null; // Specific error for mark read/delete actions
}

const initialState: NotificationsState = {
    notifications: [],
    unreadCount: 0,
    unreadMessageCount: 0,
    unreadOtherCount: 0,
    loading: false,
    error: null,
    statusLoading: {},
    actionError: null, // Initialize actionError
};

// --- Async Thunks --- (Implement API calls)

// Thunk to fetch all notifications for the logged-in user
export const fetchNotifications = createAsyncThunk<Notification[], string>(
    "notifications/fetchNotifications",
    async (userId, { rejectWithValue }) => {
        try {
            // console.log(`Fetching notifications for user ${userId}`); // Keep dev log
            // Expect the API to return the array directly
            const response = await apiClient.get<Notification[]>(`/notifications/${userId}`);
            // // console.log("response data", response.data);
            // Return the data directly, assuming it's the array
            return response?.data ?? [];
        } catch (error: unknown) {
            console.error("Error fetching notifications:", error);
            const errorPayload =
                error instanceof Error ? error.message : "Failed to fetch notifications";
            return rejectWithValue(errorPayload);
        }
    },
);

// Thunk to fetch the count of unread notifications
export const fetchUnreadCount = createAsyncThunk<number, string>(
    "notifications/fetchUnreadCount",
    async (userId, { rejectWithValue }) => {
        try {
            console.log(`Checking unread count for user ${userId}`); // Keep dev log
            const response = await apiClient.get<{ unreadCount: number }>(
                `/checkNotifications/${userId}`,
            ); // Assuming response format { count: number }
            // console.log("response data", response.data);
            // console.log("Unread count:", response.data.unreadCount);
            return response.data.unreadCount ?? 0; // Return the count, default to 0
        } catch (error: unknown) {
            console.error("Error fetching unread count:", error);
            const errorPayload =
                error instanceof Error ? error.message : "Failed to fetch unread count";
            return rejectWithValue(errorPayload);
        }
    },
);

// Thunk to mark a notification as read
export const markNotificationRead = createAsyncThunk<
    { notificationId: string }, // Return notificationId on success
    string, // Argument is notificationId
    { rejectValue: string } // Type for rejectWithValue
>(
    "notifications/markNotificationRead",
    async (
        notificationId,
        { rejectWithValue },
    ): Promise<{ notificationId: string } | ReturnType<typeof rejectWithValue>> => {
        try {
            console.log(`Marking notification ${notificationId} as read`); // Keep dev log
            // Make PUT request with status: 'read' in the body
            // await apiClient.put(`/notifications/${notificationId}`, { status: "read" });
            await apiClient.post(`/updateNotificationStatus/${notificationId}`, { status: "read" });
            return { notificationId }; // Return ID for reducer optimistic update
        } catch (error: unknown) {
            console.error("Error marking notification read:", error);
            const errorPayload =
                error instanceof Error ? error.message : "Failed to mark notification as read";
            return rejectWithValue(errorPayload);
        }
    },
);

// Thunk to delete a notification
export const deleteNotification = createAsyncThunk<
    { notificationId: string }, // Return notificationId on success
    string, // Argument is notificationId
    { rejectValue: string } // Type for rejectWithValue
>(
    "notifications/deleteNotification",
    async (
        notificationId,
        { rejectWithValue },
    ): Promise<{ notificationId: string } | ReturnType<typeof rejectWithValue>> => {
        try {
            console.log(`Deleting notification ${notificationId}`); // Keep dev log
            // await apiClient.delete(`/notifications/${notificationId}`);
            await apiClient.post(`/deleteNotification/${notificationId}`);
            return { notificationId }; // Return ID for reducer optimistic update
        } catch (error: unknown) {
            console.error("Error deleting notification:", error);
            const errorPayload =
                error instanceof Error ? error.message : "Failed to delete notification";
            return rejectWithValue(errorPayload);
        }
    },
);

// --- Slice Definition ---
const notificationsSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        // Optional: Reducer to clear notifications on logout, etc.
        clearNotifications: (state) => {
            state.notifications = [];
            state.unreadCount = 0;
            state.error = null;
            state.loading = false;
            state.actionError = null; // Clear action error on full clear
        },
        // Optional: Add a reducer to clear just the actionError
        clearActionError: (state) => {
            state.actionError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchNotifications
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchNotifications.fulfilled,
                (state, action: PayloadAction<Notification[]>) => {
                    state.loading = false;
                    state.notifications = action.payload;
                    // Calculate split unread counts
                    let messageCount = 0;
                    let otherCount = 0;
                    action.payload.forEach((n) => {
                        if (n.status === "unread") {
                            if (n.type === "message") {
                                messageCount++;
                            } else {
                                otherCount++;
                            }
                        }
                    });
                    state.unreadMessageCount = messageCount;
                    state.unreadOtherCount = otherCount;
                    state.unreadCount = messageCount + otherCount; // Update total count
                },
            )
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // fetchUnreadCount
            .addCase(fetchUnreadCount.fulfilled, (state, action: PayloadAction<number>) => {
                state.unreadCount = action.payload;
            })
            .addCase(fetchUnreadCount.rejected, (state, action) => {
                console.error("Fetch unread count failed:", action.payload); // Log error but don't set state.error
            })

            // markNotificationRead
            .addCase(markNotificationRead.pending, (state, action) => {
                state.statusLoading[action.meta.arg] = true;
                state.actionError = null; // Clear previous action error
            })
            .addCase(markNotificationRead.fulfilled, (state, action) => {
                const { notificationId } = action.payload;
                const notification = state.notifications.find(
                    (n) => n.notificationId === notificationId,
                );
                if (notification && notification.status === "unread") {
                    // Decrement the correct count BEFORE changing status
                    if (notification.type === "message") {
                        state.unreadMessageCount = Math.max(0, state.unreadMessageCount - 1);
                    } else {
                        state.unreadOtherCount = Math.max(0, state.unreadOtherCount - 1);
                    }
                    state.unreadCount = Math.max(0, state.unreadCount - 1); // Keep total count update
                    notification.status = "read"; // Now change status
                }
                delete state.statusLoading[notificationId];
                state.actionError = null; // Clear error on success
            })
            .addCase(markNotificationRead.rejected, (state, action) => {
                const notificationId = action.meta.arg;
                console.error("Mark read failed:", action.payload);
                delete state.statusLoading[notificationId];
                // Set specific action error
                state.actionError = {
                    notificationId: notificationId,
                    message: action.payload as string,
                };
            })

            // deleteNotification
            .addCase(deleteNotification.pending, (state, action) => {
                state.statusLoading[action.meta.arg] = true;
                state.actionError = null; // Clear previous action error
            })
            .addCase(deleteNotification.fulfilled, (state, action) => {
                const { notificationId } = action.payload;
                const index = state.notifications.findIndex(
                    (n) => n.notificationId === notificationId,
                );
                if (index !== -1) {
                    // Check status and type BEFORE deleting
                    if (state.notifications[index].status === "unread") {
                        if (state.notifications[index].type === "message") {
                            state.unreadMessageCount = Math.max(0, state.unreadMessageCount - 1);
                        } else {
                            state.unreadOtherCount = Math.max(0, state.unreadOtherCount - 1);
                        }
                        state.unreadCount = Math.max(0, state.unreadCount - 1); // Keep total count update
                    }
                    state.notifications.splice(index, 1); // Now remove from list
                }
                delete state.statusLoading[notificationId];
                state.actionError = null; // Clear error on success
            })
            .addCase(deleteNotification.rejected, (state, action) => {
                const notificationId = action.meta.arg;
                console.error("Delete notification failed:", action.payload);
                delete state.statusLoading[notificationId];
                // Set specific action error
                state.actionError = {
                    notificationId: notificationId,
                    message: action.payload as string,
                };
            }); // End of builder chain
    },
});

export const { clearNotifications, clearActionError } = notificationsSlice.actions;
export default notificationsSlice.reducer;
